import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

// Status taxonomy (matches the fresno_trailer_units CHECK constraint).
const STATUSES = ['Available', 'Pending', 'Sold', 'Hold', 'In Shop'];
const STATUS_STYLE = {
  Available: 'bg-green-100 text-green-800',
  Pending:   'bg-amber-100 text-amber-800',
  Sold:      'bg-navy-100 text-navy',
  Hold:      'bg-panel text-muted',
  'In Shop': 'bg-blue-100 text-blue-800',
};

// The 10 confirmed columns, in source-spreadsheet order. `key` maps to the
// API/DB field; money:true renders as currency.
const COLUMNS = [
  { key: 'stock_number',            label: 'Stock #' },
  { key: 'vin',                     label: 'VIN' },
  { key: 'description',             label: 'Description' },
  { key: 'our_cost',                label: 'Our Cost',             money: true },
  { key: 'sell_price',              label: 'Sell Price',           money: true },
  { key: 'sell_price_with_tk_s600', label: 'Sell Price w/ TK S-600', money: true },
  { key: 'sell_price_with_7700',    label: 'Sell Price w/ 7700',   money: true },
  { key: 'status',                  label: 'Status' },
  { key: 'carrier_7700_2026',       label: 'Carrier 7700 2026',    money: true },
  { key: 'tk_s600_2026',            label: 'TK S-600 2026',        money: true },
  { key: 'assigned_salesman_name',  label: 'Salesman' },
];

const money = (v) => (v == null || v === '')
  ? '—'
  : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export default function FresnoInventory() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('can_edit_customers');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [agingThreshold, setAgingThreshold] = useState(30);
  const [editing, setEditing] = useState(null); // unit row, or {} for new, or null

  const list = useQuery({
    queryKey: ['fresno-inventory', status, q],
    queryFn: () => api.get('/fresno-inventory', {
      params: { status: status || undefined, q: q || undefined },
    }).then(r => r.data.data),
  });

  const units = list.data || [];
  const available = units.filter(u => u.status === 'Available').length;
  const aging = units.filter(u => u.status === 'Available' && (u.days_in_inventory || 0) >= agingThreshold).length;

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Trailer Sales</div>
        <h1 className="text-2xl">Fresno inventory</h1>
        <div className="rule-crimson" />
        <p className="text-xs text-muted mt-1">
          Fresno trailer units. Bundled reefer prices are stored as entered — they include an adder and don’t reconcile to base + unit.
        </p>
      </header>

      <div className="card p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <div>
          <label className="label">Status</label>
          <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Aging threshold (days)</label>
          <select className="input" value={agingThreshold} onChange={e => setAgingThreshold(parseInt(e.target.value, 10))}>
            {[14, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d}d</option>)}
          </select>
        </div>
        <div>
          <label className="label">Search</label>
          <input className="input" type="search" placeholder="Stock #, VIN, description…"
                 value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {canManage && (
          <div className="flex justify-end">
            <button className="btn-primary" onClick={() => setEditing({})}>+ Add unit</button>
          </div>
        )}
      </div>

      <div className="text-sm text-muted">
        {units.length} unit{units.length === 1 ? '' : 's'}{status ? '' : ` · ${available} available`}
        {aging > 0 && <span className="text-crimson font-medium"> · {aging} aging ≥{agingThreshold}d</span>}
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-navy-100">
              {COLUMNS.map(c => (
                <th key={c.key} className={`px-3 py-2 ${c.money ? 'text-right' : ''}`}>{c.label}</th>
              ))}
              {canManage && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={COLUMNS.length + 1} className="px-3 py-6 text-muted">Loading…</td></tr>
            )}
            {!list.isLoading && units.length === 0 && (
              <tr><td colSpan={COLUMNS.length + 1} className="px-3 py-6 text-muted">
                No units{q || status ? ' match your filters.' : ' yet — add one or run the import.'}
              </td></tr>
            )}
            {units.map(u => {
              const isAging = u.status === 'Available' && (u.days_in_inventory || 0) >= agingThreshold;
              return (
              <tr key={u.id} className={`border-b border-navy-100/60 hover:bg-panel ${isAging ? 'bg-rose-50/40' : ''}`}>
                {COLUMNS.map(c => {
                  if (c.key === 'status') {
                    return (
                      <td key={c.key} className="px-3 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_STYLE[u.status] || 'bg-panel'}`}>
                          {u.status}
                        </span>
                        {isAging && (
                          <span className="ml-1 text-[10px] text-crimson font-semibold">{u.days_in_inventory}d</span>
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className={`px-3 py-2 ${c.money ? 'text-right tabular-nums' : ''}`}>
                      {c.money ? money(u[c.key]) : (u[c.key] || '—')}
                    </td>
                  );
                })}
                {canManage && (
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-3">
                    <button className="text-navy hover:underline" onClick={() => setEditing(u)}>Edit</button>
                    <Link to="/notes/new" className="text-muted hover:underline text-[11px]">Log note</Link>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <UnitEditor
          unit={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const MONEY_FIELDS = [
  'our_cost', 'sell_price', 'sell_price_with_tk_s600', 'sell_price_with_7700',
  'carrier_7700_2026', 'tk_s600_2026',
];

function UnitEditor({ unit, onClose, onSaved }) {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    stock_number: unit?.stock_number || '',
    vin: unit?.vin || '',
    description: unit?.description || '',
    our_cost: unit?.our_cost ?? '',
    sell_price: unit?.sell_price ?? '',
    sell_price_with_tk_s600: unit?.sell_price_with_tk_s600 ?? '',
    sell_price_with_7700: unit?.sell_price_with_7700 ?? '',
    status: unit?.status || 'Available',
    carrier_7700_2026: unit?.carrier_7700_2026 ?? '',
    tk_s600_2026: unit?.tk_s600_2026 ?? '',
    assigned_salesman_id: unit?.assigned_salesman_id ?? '',
  }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const salespersons = useQuery({
    queryKey: ['opportunities', 'salespersons'],
    queryFn: () => api.get('/opportunities/salespersons').then(r => r.data.data),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        stock_number: form.stock_number,
        vin: form.vin || null,
        description: form.description || null,
        status: form.status,
        assigned_salesman_id: form.assigned_salesman_id === '' ? null : Number(form.assigned_salesman_id),
      };
      for (const k of MONEY_FIELDS) payload[k] = form[k] === '' ? null : Number(form[k]);
      if (unit) return api.put(`/fresno-inventory/${unit.id}`, payload).then(r => r.data.data);
      return api.post('/fresno-inventory', payload).then(r => r.data.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fresno-inventory'] }); onSaved?.(); },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/fresno-inventory/${unit.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fresno-inventory'] }); onSaved?.(); },
  });

  const moneyField = (k, label) => (
    <div>
      <label className="label">{label}</label>
      <input className="input" type="number" step="0.01" value={form[k]}
             onChange={e => set(k, e.target.value)} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{unit ? `Edit ${unit.stock_number}` : 'Add unit'}</h3>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Stock # *</label>
            <input className="input" value={form.stock_number} autoFocus
                   onChange={e => set('stock_number', e.target.value)} />
          </div>
          <div>
            <label className="label">VIN</label>
            <input className="input" value={form.vin} onChange={e => set('vin', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="label">Description</label>
          <input className="input" value={form.description}
                 onChange={e => set('description', e.target.value)} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {moneyField('our_cost', 'Our Cost')}
          {moneyField('sell_price', 'Sell Price')}
          {moneyField('sell_price_with_tk_s600', 'Sell Price w/ TK S-600')}
          {moneyField('sell_price_with_7700', 'Sell Price w/ 7700')}
          {moneyField('carrier_7700_2026', 'Carrier 7700 2026')}
          {moneyField('tk_s600_2026', 'TK S-600 2026')}
        </div>

        <div>
          <label className="label">Status</label>
          <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <label className="label">Assigned Salesman</label>
          <select className="input" value={form.assigned_salesman_id}
                  onChange={e => set('assigned_salesman_id', e.target.value)}>
            <option value="">— Unassigned —</option>
            {(salespersons.data || []).map(s => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </select>
        </div>

        {save.isError && (
          <div className="text-crimson text-sm">{save.error?.response?.data?.error || 'Save failed'}</div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {unit && hasPermission('can_edit_customers') && (
            <button className="text-crimson text-sm"
                    onClick={() => { if (window.confirm('Delete this unit?')) remove.mutate(); }}>
              Delete
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={!form.stock_number.trim() || save.isPending}
                    onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
